/** The one row shape the Your leagues sheet draws in both sections (ISSUE-907), moved out of
 *  LeagueMenuDialog.tsx so PinnedLeagueList.tsx can draw it inside a sortable list (ISSUE-977). */
import { Checkbox, cn, FOCUS_RING } from "@sportsmet/ui";
import { type CSSProperties, type ReactNode } from "react";

import {
  COMING_SOON_LABEL,
  type typeLeagueSelectorOption,
} from "./leagueSelectorContract";

/** The drill-down arrow on every league row. Drawn, not typed (ISSUE-926).
 *  Hidden from assistive tech: the Open button around it carries the name a
 *  screen reader reads. `stroke="currentColor"` lets the button's text
 *  color drive it. */
const Chevron = (): ReactNode => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m8 5 5 5-5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The box every icon-only button on a league row shares: 44px square, the
 * platform floor, so each one is separately tappable at phone width instead
 * of being a bare glyph a thumb away from its neighbor.
 *
 * NOT the same recipe as the Dialog close button, which is what the comment
 * here used to claim. That button carries `-m-2` ahead of the same classes
 * (`packages/ui/src/Dialog/Dialog.tsx:197`), which pulls its 44px box back
 * out over the header's `p-4` so the icon lands flush with the padding edge.
 * These rows have no padding of their own to pull back over. They sit
 * straight inside the `px-4` scroll body at `Dialog.tsx:207`, so the same
 * negative margin would hang the button 8px into the sheet's gutter and sit
 * the reorder arrows on the sheet edge, which is the shape ISSUE-907's second
 * defect describes. It is left off on purpose. The color, radius and focus
 * halves are shared.
 *
 * Since ISSUE-977 a pinned row carries a single grip handle in this box where the two reorder arrows sat; the reasoning above holds for it and for the Open button.
 */
export const ICON_BUTTON_CLASS = cn(
  "flex h-touch-target w-touch-target shrink-0 items-center justify-center",
  "rounded-sm text-ink-secondary hover:bg-surface-sunken hover:text-ink",
  FOCUS_RING,
);

/** ISSUE-977. The lifted look a pinned row takes on while it is being
 *  dragged: raised above its neighbors with a shadow. */
const LIFTED_ROW_CLASS =
  "relative z-10 rounded-md bg-surface-elevated shadow-lg";

export type typeLeagueRowProps = {
  option: typeLeagueSelectorOption;
  isPinned: boolean;
  onTogglePin: () => void;
  onOpen: () => void;
  reorder?: ReactNode;
  /** ISSUE-977. dnd-kit's node ref for a sortable pinned row. Absent on a sport row. */
  rowRef?: ((node: HTMLElement | null) => void) | undefined;
  /** ISSUE-977. The sortable transform and transition while a drag is running. */
  rowStyle?: CSSProperties | undefined;
  /** ISSUE-977. True on the row being dragged: lifted above its neighbors. */
  isLifted?: boolean | undefined;
};

/**
 * One league, drawn the same way whether it sits in the pinned section or
 * under its sport. The checkbox carries the name and is the pin switch, so a
 * pinned row can be unpinned where it stands; `reorder` is the only part that
 * differs, and only a pinned row passes it.
 *
 * "Coming soon" is the Checkbox's `description`, under the name in the same
 * column, and NOT a sibling flex item beside it. It used to be the sibling,
 * held to one line with `shrink-0` (ISSUE-907 R4), and that worked while the
 * only row carrying it was a sport-section row: 358px of `li` at 390px, less
 * the 44px Open button and two gaps, left the checkbox column 212px, enough
 * for "NCAA Basketball" at 128px. A pinned row now carries the two 44px
 * reorder arrows as well, and measured in Chromium at 390px that same recipe
 * left the column 108px, 72px of it for text, so every two-word launch name
 * ("NCAA FBS", "NCAA FCS", "NCAA DII/III", "NCAA Basketball", "Premier
 * League") wrapped onto a second line the moment it was both pinned and
 * flagged coming soon. Which league is coming soon is an admin switch
 * (`visibilityState`), not a launch constant, so that state is reachable.
 *
 * Under the name, the label costs the row nothing sideways: the column keeps
 * 202px in a pinned row and 306px in a sport row, and the widest launch name
 * sits on one line in both. The row grows to about 56px instead, a deliberate
 * second line in caption ink, and the arrows and the Open button stay centered
 * on it. It also puts the label on the control's `aria-describedby`, so a
 * screen reader hears "Coming soon" with the checkbox instead of as loose text
 * after it.
 *
 * `data-testid` is `league-menu-league` in both sections on purpose. See the
 * test-id note in this file's header before changing it.
 *
 * `rowRef`, `rowStyle` and `isLifted` are the sortable wiring PinnedLeagueList.tsx passes for a pinned row (ISSUE-977); a sport row passes none of them.
 *
 * Since ISSUE-977 a pinned row carries one 44px grip handle where it carried the two 44px reorder arrows, so the widths above describe the old, narrower row; leagueMenuGeometry.e2e.ts re-measures the new one.
 */
export const LeagueRow = (props: typeLeagueRowProps): ReactNode => (
  <li
    ref={props.rowRef}
    style={props.rowStyle}
    data-testid="league-menu-league"
    data-league-id={props.option.leagueId}
    className={cn(
      "flex min-h-touch-target items-center gap-2",
      props.isLifted === true && LIFTED_ROW_CLASS,
    )}
  >
    <Checkbox
      label={props.option.shortName}
      description={props.option.isComingSoon ? COMING_SOON_LABEL : undefined}
      checked={props.isPinned}
      onCheckedChange={props.onTogglePin}
    />
    {props.reorder}
    {/* The checkbox to the left already prints the league name, so this
        button shows the drill-down arrow instead of a second copy of it. The
        name lives on in `aria-label`, which is the whole accessible name of
        the button, so a screen reader still hears exactly which league it
        opens. */}
    <button
      type="button"
      aria-label={`Open ${props.option.shortName}`}
      onClick={props.onOpen}
      className={ICON_BUTTON_CLASS}
    >
      <Chevron />
    </button>
  </li>
);
